import { createFileRoute, redirect } from '@tanstack/react-router';

import { integrationsCatalog } from '@mint-lib/integrations-catalog';
import { GoogleAnalyticsIntegrations } from '@mint-sdk/api-integrations';
import {
  AdserverUrnEnum,
  TRAFFICKING_PLATFORM_URN,
} from '@mint-sdk/api-integrations/compat';
import { Connection } from '@mint-sdk/api-omni';
import {
  Integrations,
  LocalStorage,
  Utils,
} from '@mint-sdk/api-omni-trafficking';
import { z } from 'zod';

export const Route = createFileRoute(
  '/_secured/external-data/integrations/$integration/connections/add',
)({
  validateSearch: z.object({
    state: z.string().optional(),
    code: z.string().optional(),
    oauth_token: z.string().optional(),
    oauth_verifier: z.string().optional(),
    oauth_result: z.string().optional(),
  }),
  async beforeLoad({ context, params, search }) {
    const { integration } = params;

    // PDT-102320/PDT-102560: registry path. Additive — the legacy body below
    // is untouched and still runs verbatim unless both `integrationRegistry` and
    // `settingsMint2` are on.
    const isRegistry = (
      await Promise.all([
        context.isFlagEnabled('@mint/arm', 'integrationRegistry'),
        context.isFlagEnabled('@mint/arm', 'settingsMint2'),
      ])
    ).every(Boolean);
    if (isRegistry) {
      const definition = integrationsCatalog.get(integration);
      const redirectTarget = definition?.connection?.oauth?.resumeRedirect?.({
        params,
        search,
      });
      const connectionId = redirectTarget?.params?.connection;
      if (connectionId) {
        throw redirect({
          to: '/external-data/integrations/$integration/connections/$connection/update',
          params: { integration, connection: connectionId },
          search,
        });
      }
      return;
    }

    const { state, code } = search;

    // Legacy (client-owned OAuth) CM360 reconnect handoff, restored
    // behind the cm360BackendOwnedAuth flag. While the flag is off,
    // Google's redirect lands back on this Add route even for a
    // reconnect (not the Update route), so this recovers the
    // in-progress connection from localStorage and forwards there. The
    // backend-owned flow instead redirects the browser straight to the
    // Update route itself, so this is a no-op once the flag is on.
    const isCampaignManager =
      integration === TRAFFICKING_PLATFORM_URN.campaignManager;
    if (state && code && isCampaignManager) {
      const isBackendOwnedAuth = await context.isFlagEnabled(
        '@mint/arm',
        'cm360BackendOwnedAuth',
      );
      if (!isBackendOwnedAuth) {
        const connectionId = LocalStorage.getConnectionIdFrom(
          TRAFFICKING_PLATFORM_URN.campaignManager,
        );
        const oauthFlowId = LocalStorage.getOauthFlowIdFrom(
          TRAFFICKING_PLATFORM_URN.campaignManager,
          state,
        );
        if (connectionId && oauthFlowId) {
          throw redirect({
            to: '/external-data/integrations/$integration/connections/$connection/update',
            params: { integration, connection: connectionId },
            search,
          });
        }
      }
    }
  },
  loader: async ({ context, params }) => {
    const isRegistry = (
      await Promise.all([
        context.isFlagEnabled('@mint/arm', 'integrationRegistry'),
        context.isFlagEnabled('@mint/arm', 'settingsMint2'),
      ])
    ).every(Boolean);
    const isReadOnly = !(await context.hasPermission('integrations:modify'));
    const { integration } = params;

    const queryClient =
      await context.serviceLocator.getOrThrowInstance('queryClient');

    if (isRegistry) {
      const definition = integrationsCatalog.get(integration);
      if (!definition?.connection) return;

      // Each definition's own i18n namespace, loaded alongside its query
      // prefetch — mirrors the legacy path's `getInstance('i18n', ...)` so
      // the mounted `AddForm` never suspends on translations it could have
      // had ready by the time the loader resolves.
      const prefetched = await definition.connection.prefetch?.({
        queryClient,
        params: { integration },
      });
      await context.getInstance('i18n', definition.meta.i18nNamespace);
      await context.getInstance('i18n', '@myn/connections');
      const firstConnection = prefetched?.connections?.[0];
      if (isReadOnly && firstConnection) {
        throw redirect({
          to: '/external-data/integrations/$integration/connections/$connection/update',
          params: { integration, connection: firstConnection.id },
        });
      }
      return;
    }

    await context.getInstance('i18n', '@myn/integrations_v2');

    let connectionList;
    if (Utils.getAdServerPlatformFrom(integration)) {
      connectionList = await queryClient.ensureQueryData(
        Integrations.getIntegrationList({
          params: {
            adServer: AdserverUrnEnum.safeParse(integration).data ?? null,
          },
        }),
      );
    } else if (
      // will be removed after refactoring and separating trafficking integrations
      integration === TRAFFICKING_PLATFORM_URN.googleAnalytics
    ) {
      connectionList = await queryClient.ensureQueryData(
        GoogleAnalyticsIntegrations.getGA4Integrations({}),
      );
    } else {
      connectionList = await queryClient.ensureQueryData(
        Connection.getConnectionList({
          params: {
            platform: integration,
          },
        }),
      );
    }

    if (isReadOnly && connectionList?.[0]) {
      throw redirect({
        to: '/external-data/integrations/$integration/connections/$connection/update',
        params: {
          integration,
          connection: connectionList[0].id,
        },
      });
    }
  },
});
